/**
 * 멍투데이 시스템 공지사항 - 보호자앱용
 *
 * 백엔드 API 명세서 §3 기반. 토큰의 userType=USER 사용자 전용.
 * 서버에서 USER → GUARDIAN 매핑하여 targetApp=GUARDIAN 공지만 조회됨.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { privateHttp } from "~/config/axios";
import { API_ENDPOINTS } from "~/config/endpoints";

// ─────────────────────────────────────────────────────────────
// 타입
// ─────────────────────────────────────────────────────────────

export type AnnouncementCategory =
  | "SYSTEM"
  | "MAINTENANCE"
  | "EVENT"
  | "GENERAL";

export interface AnnouncementItem {
  id: number;
  category: AnnouncementCategory;
  title: string;
  contentHtml: string;
  isImportant: boolean;
  isPinned: boolean;
  displayStartAt?: string | null;
  displayEndAt?: string | null;
  createdAt: string;
}

export interface AnnouncementListItem extends AnnouncementItem {
  isRead: boolean;
}

interface ServiceResult<T> {
  codeEnum: { value: string; desc: string; status: number };
  data: T;
}

// ─────────────────────────────────────────────────────────────
// React Query 키
// ─────────────────────────────────────────────────────────────

export const customerAnnouncementKeys = {
  list: ["customer-announcement-list"] as const,
  detail: (id: number | undefined) =>
    ["customer-announcement-detail", id] as const,
  unread: ["customer-announcement-unread"] as const,
};

// ─────────────────────────────────────────────────────────────
// API 호출
// ─────────────────────────────────────────────────────────────

async function fetchList(): Promise<AnnouncementListItem[]> {
  const { data } = await privateHttp.get<ServiceResult<AnnouncementListItem[]>>(
    API_ENDPOINTS.USER_ANNOUNCEMENT.LIST,
  );
  return data.data;
}

async function fetchDetail(id: number): Promise<AnnouncementListItem> {
  const { data } = await privateHttp.get<ServiceResult<AnnouncementListItem>>(
    `${API_ENDPOINTS.USER_ANNOUNCEMENT.DETAIL}/${id}`,
  );
  return data.data;
}

async function fetchUnreadCount(): Promise<{ count: number }> {
  const { data } = await privateHttp.get<ServiceResult<{ count: number }>>(
    API_ENDPOINTS.USER_ANNOUNCEMENT.UNREAD_COUNT,
  );
  return data.data;
}

async function postMarkAsRead(id: number): Promise<void> {
  await privateHttp.post(`${API_ENDPOINTS.USER_ANNOUNCEMENT.READ}/${id}/read`);
}

// ─────────────────────────────────────────────────────────────
// React Query hooks
// ─────────────────────────────────────────────────────────────

export const useCustomerAnnouncementList = () => {
  return useQuery({
    queryKey: customerAnnouncementKeys.list,
    queryFn: fetchList,
    staleTime: 30 * 1000,
  });
};

export const useCustomerAnnouncementDetail = (id: number | undefined) => {
  return useQuery({
    queryKey: customerAnnouncementKeys.detail(id),
    queryFn: () =>
      id == null
        ? Promise.resolve(null as AnnouncementListItem | null)
        : fetchDetail(id),
    enabled: id != null && !Number.isNaN(id),
  });
};

export const useCustomerAnnouncementUnreadCount = () => {
  return useQuery({
    queryKey: customerAnnouncementKeys.unread,
    queryFn: fetchUnreadCount,
    staleTime: 30 * 1000,
    refetchOnWindowFocus: true,
  });
};

export const useCustomerAnnouncementMarkAsRead = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: postMarkAsRead,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: customerAnnouncementKeys.list,
      });
      queryClient.invalidateQueries({
        queryKey: customerAnnouncementKeys.unread,
      });
    },
  });
};
